import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Stack,
  Typography,
  CircularProgress,
  Grid,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PendingOutlinedIcon from "@mui/icons-material/PendingOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { getVersionView } from "../../api/versionHistoryView";
import {
  formatOneDecimal,
  formatThreeDecimals,
  formatValueNoDecimals,
} from "../utils/formatTwoDecimals";

const VersionHistoryView = ( { id, toggleHistory, s } ) => {
  const [ versionHistory, setVersionHistory ] = useState( [] );
  const [ loading, setLoading ] = useState( false );
  const [ errorMsg, setErrorMsg ] = useState( "" );
 const [hasHistory, setHasHistory] = useState(false);
  // Track which cards are expanded (Set of keys)
  const [ expandedKeys, setExpandedKeys ] = useState( () => new Set() );

  const isOpen = !!s?.expandedHistory;

  const getCurrencySymbol = ( code ) => {
    if ( !code ) return "";
    try {
      return ( 0 )
        .toLocaleString( "en", { style: "currency", currency: code, minimumFractionDigits: 0, maximumFractionDigits: 0 } )
        .replace( /[\d,\s.]/g, "" )
        .trim();
    } catch {
      return code;
    }
  };

  const fetchVersionHistory = async () => {
  if (!s?.materialPlant || !s?.materialNumber) return;

  setLoading(true);
  setErrorMsg("");

  try {
    const response = await getVersionView(
      s.materialPlant,
      s.materialNumber
    );

    if (Array.isArray(response) && response.length > 0) {
      setVersionHistory(response);
      setHasHistory(true);
      setExpandedKeys(new Set());
    } else {
      setVersionHistory([]);
      setHasHistory(false);
    }
  } catch (err) {
    setErrorMsg("Something went wrong while loading version history.");
    setVersionHistory([]);
    setHasHistory(false);
  } finally {
    setLoading(false);
  }
};

  useEffect( () => {
    if ( isOpen ) {
      fetchVersionHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ isOpen, s?.materialPlant, s?.approverId ] );

  // ---------- Helpers (format + UI bits) ----------
  const n = ( v ) => {
    const num = Number( v );
    return isFinite( num ) ? num : 0;
  };
  const fmtNum = ( v ) => formatOneDecimal( v );
  const fmtRate = ( v ) => formatThreeDecimals( v );
  const fmtQty = ( v ) => formatValueNoDecimals( v );
  const fmtPct = ( v ) =>
    v === null || v === undefined || v === "" || isNaN( Number( v ) )
      ? "—"
      : `${ Math.round( Number( v ) ) }%`;
  const val = ( v ) =>
    v === null || v === undefined || v === "" ? "—" : String( v );

  const statusChip = ( status ) => {
    if ( !status )
      return (
        <Chip size="small" label="Unknown" color="default" variant="outlined" />
      );
    const s = String( status ).toLowerCase();
    if ( s.includes( "approve" ) && s.includes( "pending" ) ) {
      return (
        <Chip
          size="small"
          label="Pending Approval"
          color="warning"
          icon={ <PendingOutlinedIcon /> }
          variant="filled"
        />
      );
    }
    if ( s.includes( "approved" ) ) {
      return (
        <Chip
          size="small"
          label="Approved"
          color="success"
          icon={ <CheckCircleOutlineIcon /> }
          variant="filled"
        />
      );
    }
    if ( s.includes( "reject" ) ) {
      return (
        <Chip
          size="small"
          label="Rejected"
          color="error"
          icon={ <CancelOutlinedIcon /> }
          variant="filled"
        />
      );
    }
    return (
      <Chip size="small" label={ status } color="default" variant="outlined" />
    );
  };

  const choiceChip = ( choice, label = "Choice" ) => {
    if ( !choice )
      return <Chip size="small" label={ `${ label }: —` } variant="outlined" />;
    const c = String( choice ).toUpperCase();
    const color = c.includes( "APPROVE" )
      ? "success"
      : c.includes( "REJECT" )
        ? "error"
        : c.includes( "ACCEPT_IO" )
          ? "info"
          : c.includes( "NEW PROPOSAL" )
            ? "primary"
            : "default";
    return (
      <Chip
        size="small"
        label={ `${ label }: ${ choice }` }
        color={ color }
        variant="outlined"
      />
    );
  };

  const Section = ( { title, children } ) => (
    <Box sx={ { mb: 2 } }>
      <Typography
        variant="subtitle2"
        sx={ { color: "text.secondary", fontWeight: 600, mb: 1 } }
      >
        { title }
      </Typography>
      <Box>{ children }</Box>
    </Box>
  );

  const Field = ( { label, value, tooltip } ) => (
    <Stack direction="row" spacing={ 1 } alignItems="center" sx={ { py: 0.5 } }>
      <Typography
        variant="body2"
        sx={ { color: "text.secondary", minWidth: 200 } }
      >
        { label }
      </Typography>
      <Typography variant="body2" sx={ { fontWeight: 600 } }>
        { value }
      </Typography>
      { tooltip ? (
        <Tooltip title={ tooltip }>
          <InfoOutlinedIcon fontSize="small" sx={ { color: "text.disabled" } } />
        </Tooltip>
      ) : null }
    </Stack>
  );

  // --------- PDF formulas -> computed fields ----------
  // Source: "Formula.pdf" provided in the thread. (We follow all formulas and naming from that document.)
  const computeDerived = useCallback( ( raw ) => {
    // Keep full precision in derived math to match Output/Admin behavior.
    const weekly = n( raw.weekelyDemandQty ?? raw.weeklyDemandQty );
    const pdrPerEa = n( raw.partPerUnitEA ?? raw.partUnitsPerEa );
    const s4ConsWk = n( raw.s4ConsolidatedSafetyStock );
    const ioConsWkRounded = n( raw.ioConsolidatedSafetyStockRounded );
    const apprConsWk = n( raw.approvedConsolidatedSafetyStock );
    const userConsWk = n( raw.userConsolidatedSafetyStock );

    // Match AdminConsolidate standard price display behavior (default USD mode).
    const pricePerUnitVal = n( raw.pricePerUnit );
    const exchangeRateVal = n( raw.exchangeRate );
    const standardPriceVal = n( raw.standardPrice );
    const isUSD = String( raw.currency ?? "" ).toUpperCase() === "USD";
    const stdPricePerQty =
      pricePerUnitVal <= 0
        ? 0
        : !isUSD
          ? standardPriceVal / pricePerUnitVal
          : exchangeRateVal >= 0
            ? standardPriceVal * exchangeRateVal / pricePerUnitVal
            : standardPriceVal / ( pricePerUnitVal * exchangeRateVal );

    // Consolidated values used by variance formulas.
    const apprUsdCalc = apprConsWk * weekly * stdPricePerQty;
    const s4UsdCalc = s4ConsWk * weekly * stdPricePerQty;
    const ioUsdCalc = ioConsWkRounded * weekly * stdPricePerQty;

    // User local value retained for current Version History UI block.
    const userTime = n( raw.userSafetyTime );
    const isPart = String( raw.ioMaterialType || "" ).toUpperCase() === "PART";
    const userUsdCalc = ( isPart ? userConsWk : userTime ) * weekly * stdPricePerQty;

    // Always compute derived fields in UI to avoid API/UI discrepancies.
    const showVarFields = [ "End User SS Approved", "End User SS Rejected" ].includes( String( raw.lineStatus || "" ) );

    const varWkUserVsS4 = userConsWk - s4ConsWk;
    const varPctUserVsS4_isNA = s4ConsWk === 0;
    const varPctUserVsS4 = varPctUserVsS4_isNA ? 0 : ( ( userConsWk - s4ConsWk ) / s4ConsWk ) * 100;
    const varPdrUserVsS4 = ( userConsWk - s4ConsWk ) * weekly * pdrPerEa;
    const varUsdUserVsS4 = userUsdCalc - s4UsdCalc;

    const varWkApprovedSSvsS4 = apprConsWk - s4ConsWk;
    const varPctApprovedSSvsS4_isNA = s4ConsWk === 0;
    const varPctApprovedSSvsS4 = varPctApprovedSSvsS4_isNA ? 0 : ( ( apprConsWk - s4ConsWk ) / s4ConsWk ) * 100;
    const varPdrApprovedSSvsS4Calc = ( apprConsWk - s4ConsWk ) * weekly * pdrPerEa;
    const varUsdApprovedSSvsS4Calc = apprUsdCalc - s4UsdCalc;
    const varUsdApprovedSSvsIOCalc = apprUsdCalc - ioUsdCalc;

    const varWkIOvsS4 = ioConsWkRounded - s4ConsWk;
    const varPctIOvsS4_isNA = s4ConsWk === 0;
    const varPctIOvsS4 = varPctIOvsS4_isNA ? 0 : ( ( ioConsWkRounded - s4ConsWk ) / s4ConsWk ) * 100;
    const varPdrIOvsS4 = ( ioConsWkRounded - s4ConsWk ) * weekly * pdrPerEa;
    const varUsdIOvsS4 = ioUsdCalc - s4UsdCalc;

    const varPctApprovedSSvsIO_isNA = ioConsWkRounded === 0;
    const varPctApprovedSSvsIO =
      varPctApprovedSSvsIO_isNA ? 0 : ( ( apprConsWk - ioConsWkRounded ) * 100 ) / ioConsWkRounded;

    const varPdrApprovedSSvsS4 = showVarFields ? varPdrApprovedSSvsS4Calc : "";
    const varUsdApprovedSSvsS4 = showVarFields ? varUsdApprovedSSvsS4Calc : "";
    const varUsdApprovedSSvsIO = showVarFields ? varUsdApprovedSSvsIOCalc : "";

    // Display follows API currency: USD => standardPrice * exchangeRate, else standardPrice.
    const standardPriceDisplay = isUSD
      ? standardPriceVal * exchangeRateVal
      : standardPriceVal;

    // MRPID = [Material Plant] & [MRP Controller]
    const mrpid = `${ val( raw.materialPlant ) } & ${ val( raw.mrpControllerId ) }`;

    return {
      // Keep originals
      ...raw,
      __rawApi: raw,

      // Recomputed (wk)
      userConsolidatedWk: userConsWk,
      approvedConsolidatedWk: apprConsWk,

      // USD values
      consolidatedS4Usd: s4UsdCalc,
      ioUsdRounded: ioUsdCalc,
      consolidatedUserUsd: userUsdCalc,
      consolidatedApprovedUsd: apprUsdCalc,

      // Variances (wk / % / PDR / USD)
      varWkUserVsS4,
      varPctUserVsS4_isNA,
      varPctUserVsS4,
      varPdrUserVsS4,
      varUsdUserVsS4,

      varWkApprovedSSvsS4,
      varPctApprovedSSvsS4_isNA,
      varPctApprovedSSvsS4,
      varPdrApprovedSSvsS4,
      varUsdApprovedSSvsS4,
      varUsdApprovedSSvsIO,
      varPctApprovedSSvsIO_isNA,
      varPctApprovedSSvsIO,

      varWkIOvsS4,
      varPctIOvsS4_isNA,
      varPctIOvsS4,
      varPdrIOvsS4,
      varUsdIOvsS4,

      // Other derived
      standardPriceDisplay,
      mrpid,

      // Normalised field aliases
      weeklyDemandQty: raw.weekelyDemandQty ?? raw.weeklyDemandQty,
      partUnitsPerEa: raw.partPerUnitEA ?? raw.partUnitsPerEa,
      s4SafetyStockQty: raw.s4SafetyStock ?? raw.s4SafetyStockQty,

      // Reason code descriptions
      userReasonDesc:
        typeof raw.userReasonCode === "object" && raw.userReasonCode !== null
          ? raw.userReasonCode.reasonDescription
          : raw.userReasonCode,
      approverReasonDesc:
        typeof raw.approverReasonCode === "object" && raw.approverReasonCode !== null
          ? raw.approverReasonCode.reasonDescription
          : raw.approverReasonCode,
    };
  }, [] );

  const list = useMemo( () => {
    const arr = versionHistory || [];
    return [ ...arr ].sort( ( a, b ) => {
      const dateA = a.writebackDate ? new Date( a.writebackDate ).getTime() : 0;
      const dateB = b.writebackDate ? new Date( b.writebackDate ).getTime() : 0;
      return dateB - dateA;
    } );
  }, [ versionHistory ] );
  const computedList = useMemo(
    () => list.map( computeDerived ),
    [ list, computeDerived ],
  );

  return (
    <Box sx={ { mt: 2 } }>
      {/* Header */ }
      <Stack direction="row" alignItems="center" spacing={ 1 }>
        <Typography variant="h6" sx={ { fontWeight: 600, flexGrow: 1 } }>
          Version History
        </Typography>

       {hasHistory && (
  <IconButton
    onClick={() => id && toggleHistory(id)}
    aria-label="toggle version history"
  >
    {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
  </IconButton>
)}
      </Stack>

      {/* Collapsible history (outer) */ }
      <Collapse in={ isOpen } timeout={ 300 } unmountOnExit>
        {/* Loading / Error / Empty states */ }
        { loading ? (
          <Stack alignItems="center" justifyContent="center" sx={ { py: 6 } }>
            <CircularProgress size={ 28 } color="#000000" />
            <Typography variant="body2" color="text.secondary" sx={ { mt: 1 } }>
              Loading version history…
            </Typography>
          </Stack>
        ) : errorMsg ? (
          <Typography color="error" sx={ { mt: 2 } }>
            { errorMsg }
          </Typography>
        ) : computedList.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={ { mt: 2 } }>
            No version history available.
          </Typography>
        ) : (
          // Scrollable container
          <Box
            sx={ {
              mt: 2,
              maxHeight: 460, // <<-- scroll height
              overflow: "auto",
              pr: 0.5,
              "&::-webkit-scrollbar": { width: 8 },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: "divider",
                borderRadius: 4,
              },
            } }
          >
            <Stack spacing={ 2 }>
              { computedList.map( ( item, idx ) => {
                const key = item.workflowId ?? idx;

                const headerTitle = `${ val( item.materialNumber ) } • ${ val( item.materialDescription ) }`;
                const subTitleLeft = `Plant ${ val( item.materialPlant ) }`;
                //const subTitleRight = `MRP: ${val(item.mrpControllerId)} • ${val(item.mrpControllerDescription)}`;
                const subTitleRight = `IO Extraction Date: ${ val( item?.ioExtractionDate ) }`;
                const expanded = expandedKeys.has( key );
                const sym = getCurrencySymbol( item.currency ?? "" );
                const fmtCurr = ( v ) => {
                  const f = formatValueNoDecimals( v );
                  return f === "-" ? "—" : `${ sym }${ f }`;
                };
                const fmtStandardPrice = () => {
                  const isUSD = String( item.currency ?? "" ).toUpperCase() === "USD";
                  if ( isUSD ) {
                    const f = formatValueNoDecimals( ( item.standardPrice ?? 0 ) * ( item.exchangeRate ?? 0 ) );
                    return f === "-" ? "—" : `$${ f }`;
                  }
                  const f = formatValueNoDecimals( item.standardPrice ?? 0 );
                  return f === "-" ? "—" : `${ getCurrencySymbol( item.currency ?? "" ) }${ f }`;
                };

                return (
                  <Card key={ key } variant="outlined" sx={ { borderRadius: 2 } }>
                    {/* Header row */ }
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={ 2 }
                      sx={ {
                        px: 2,
                        py: 1.5,
                        cursor: "pointer",
                        "&:hover": { bgcolor: "action.hover" },
                      } }
                      role="button"
                      tabIndex={ 0 }
                      onClick={ () =>
                        setExpandedKeys( ( p ) => {
                          const next = new Set( p );
                          next.has( key ) ? next.delete( key ) : next.add( key );
                          return next;
                        } )
                      }
                      onKeyDown={ ( e ) => {
                        if ( e.key === "Enter" || e.key === " " ) {
                          e.preventDefault();
                          setExpandedKeys( ( p ) => {
                            const next = new Set( p );
                            next.has( key ) ? next.delete( key ) : next.add( key );
                            return next;
                          } );
                        }
                      } }
                      aria-expanded={ expanded }
                      aria-controls={ `history-body-${ key }` }
                    >
                      <IconButton size="small" sx={ { mr: -1 } }>
                        { expanded ? <ExpandLessIcon /> : <ExpandMoreIcon /> }
                      </IconButton>

                      <Box sx={ { flexGrow: 1, minWidth: 0 } }>
                        <Typography variant="subtitle2" noWrap>
                          { headerTitle }
                        </Typography>
                        <Stack direction="column" spacing={ 1 }>
                          <Stack
                            direction="row"
                            spacing={ 1 }
                            justifyContent="space-between"
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              noWrap
                            >
                              { subTitleLeft }
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              noWrap
                            >
                              { subTitleRight }
                            </Typography>
                          </Stack>
                          <Box
                            sx={ {
                              mt: 0.75,
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 0.75,
                              rowGap: 0.75,
                            } }
                          >
                            { statusChip( item.lineStatus ) }
                            { choiceChip( item.userSelectedChoice, "User" ) }
                            { choiceChip(
                              item.approverSelectedChoice,
                              "Approver",
                            ) }
                          </Box>
                        </Stack>
                      </Box>
                    </Stack>

                    <Divider />

                    {/* Body */ }
                    <Collapse in={ expanded } timeout={ 200 }>
                      <CardContent id={ `history-body-${ key }` } sx={ { pt: 2 } }>
                        <Grid container spacing={ 2 }>
                          {/* Left column */ }
                          <Grid item xs={ 12 } md={ 6 }>
                            <Section title="Overview">
                              <Field label="Extraction Date" value={ val( item.ioExtractionDate ) } />
                              <Field label="IO Run Type" value={ val( item.ioRunTypeLabel ) } />
                              <Field label="Approval status" value={ val( item.lineStatus ) } />
                              <Field label="MRP controller" value={ val( item.mrpControllerId ) } />
                              <Field label="MRP controller desc" value={ val( item.mrpControllerDescription ) } />
                              <Field label="BCP Flag" value={ val( item.bpcFlag ) } />
                              <Field label="Material Plant" value={ val( item.materialPlant ) } />
                              <Field label="IO Material Type" value={ val( item.ioMaterialType ) } />
                              <Field label="Material Number" value={ val( item.materialNumber ) } />
                              <Field label="Material Description" value={ val( item.materialDescription ) } />
                              <Field label="Approval Required?" value={ val( item.approval_required ?? item.approvalRequired ) } />
                              <Field label="Review Flag for IO Writeback" value={ val( item.reviewRequired ) } />
                              <Field label="Approver" value={ val( item.approverName || item.approverId ) } />
                              <Field label="Standard Price (local)" value={ fmtStandardPrice() } />
                              <Field label="Price Unit" value={ fmtQty( item.pricePerUnit ) } />
                              <Field label="Exchange Rate" value={ fmtRate( item.exchangeRate ) } />
                              <Field label="Part Units Per EA" value={ fmtNum( item.partUnitsPerEa ) } />
                              <Field label="Weekly Demand Qty" value={ fmtNum( item.weeklyDemandQty ) } />
                              <Field label="SS Final (weeks)" value={ fmtNum( item.ssFinal ) } />
                              <Field label="Writeback Status" value={ val( item.writebackStatus ) } />
                              <Field label="Writeback Date" value={ val( item.writebackDate ) } />
                            </Section>

                            <Section title="S4">
                              <Field
                                label="S4 Safety Time (weeks)"
                                value={ fmtNum( item.s4SafetyTime ) }
                              />
                              <Field
                                label="S4 Safety Stock (qty)"
                                value={ fmtQty( item.s4SafetyStockQty ) }
                              />
                              <Field
                                label="S4 Coverage Profile (weeks)"
                                value={ fmtNum( item.s4CoverageProfile ) }
                              />
                              <Field
                                label="S4 consolidated SS (weeks)"
                                value={ fmtNum( item.s4ConsolidatedSafetyStock ) }
                              />
                            </Section>

                            <Section title="IO">
                              <Field
                                label="IO Safety Time (weeks)"
                                value={ fmtNum( item.ioSafetyTime ) }
                              />
                              <Field
                                label="IO Coverage Profile (weeks)"
                                value={ fmtNum( item.ioCoverageProfile ) }
                              />
                              <Field
                                label="IO consolidated SS (weeks)"
                                value={ fmtNum(
                                  item.ioConsolidatedSafetyStockRounded,
                                ) }
                              />
                            </Section>
                          </Grid>

                          {/* Right column */ }
                          <Grid item xs={ 12 } md={ 6 }>
                            <Section title="End User proposal">
                              <Field label="End User action" value={ val( item.userSelectedChoice ) } />
                              <Field label="Reason" value={ val( item.userReasonDesc ) } />
                              <Field label="Comments" value={ val( item.userComments ) } />
                              <Field label="Total consolidated Safety Stock (weeks)-EU" value={ fmtNum( item.userConsolidatedWk ) } />
                              <Field label="Safety Time (weeks)-EU" value={ fmtNum( item.userSafetyTime ) } />
                              <Field label="Coverage Profile (weeks)-EU" value={ fmtNum( item.userCoverageProfile ) } />
                              <Field label="Safety Stock (qty)-EU" value={ fmtQty( item.userSafetyStockQty ) } />
                            </Section>

                            <Section title="Approved Safety Stock">
                              <Field label="Final selected choice" value={ val( item.approverSelectedChoice ) } />
                              <Field
                                label="Total consolidated Safety Stock (weeks)-approved"
                                value={ fmtNum( item.approvedConsolidatedWk ) }
                              />
                              <Field
                                label="Safety Time (weeks)-approved"
                                value={ fmtNum( item.approvedSafetyTime ) }
                              />
                              <Field
                                label="Coverage Profile (weeks)-approved"
                                value={ fmtNum( item.approvedCoverageProfile ) }
                              />
                              <Field label="Safety Stock (qty)-approved" value={ fmtQty( item.approvedSafetyStockQty ) } />
                              <Field label="Comments from the approver" value={ val( item.approverComments ) } />
                              <Field label="Reason Approver" value={ val( item.approverReasonDesc ) } />
                              <Field label="IO SS value (local)" value={ fmtCurr( item.ioUsdRounded ) } />
                              <Field label="S4 SS value (local)" value={ fmtCurr( item.consolidatedS4Usd ) } />
                              <Field label="Approved SS value (local)" value={ fmtCurr( item.consolidatedApprovedUsd ) } />
                            </Section>

                            <Section title="Variance">
                              <Field label="VAR (value) - Approved SS value vs S4 (local)" value={ fmtCurr( item.varUsdApprovedSSvsS4 ) } />
                              <Field label="VAR (value) - Approved SS vs IO (local)" value={ fmtCurr( item.varUsdApprovedSSvsIO ) } />
                              <Field label="VAR (PDR) - Approved value vs S4" value={ fmtQty( item.varPdrApprovedSSvsS4 ) } />
                              <Field label="VAR (%) - Approved SS vs S4(weeks)" value={ item.varPctApprovedSSvsS4_isNA ? "N/A" : fmtPct( item.varPctApprovedSSvsS4 ) } />
                              <Field label="VAR (%) - Approved SS vs IO(weeks)" value={ item.varPctApprovedSSvsIO_isNA ? "N/A" : fmtPct( item.varPctApprovedSSvsIO ) } />
                            </Section>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Collapse>
                  </Card>
                );
              } ) }
            </Stack>
          </Box>
        ) }
      </Collapse>
    </Box>
  );
};

export default VersionHistoryView;
